import moment from "moment";
import { type TFunction } from "i18next";
import { type CICase, type CIReferral, CIReferralStatus } from "../interfaces";
import { getObjectComparators, type ISortsDefinition } from "components/Shared/MaterialTable";

export const isExpiredReferral = (referral?: CIReferral) => !!referral && referral.status === CIReferralStatus.Open && moment().isAfter(moment(referral.validUntil));

const momentFormatTokens = {
    year: ['YYYY', 'YY'],
    month: ['MMMM', 'MMM', 'MM', 'M'],
    day: ['dddd', 'ddd', 'DD', 'dd', 'Do', 'd', 'D'],
    hour: ['HH', 'hh', 'H', 'h'],
    minute: ['mm', 'm'],
    second: ['ss', 's']
};

export const addSecondsToFormatString = (format: string, t: TFunction): string => {
    if (!momentFormatTokens.second.some(token => format.replace(/\[([^\]]+)\]/g, '').includes(token))) {
        if (format.includes('[M]')) {
            format += ' s[S]';
        } else if (format.includes(':m')) {
            format += `:s`;
        } else {
            format += ` s [${t('seconds')}]`;
        }
    }
    return format;
};

export function formatDurationWithRollover(duration: moment.Duration, format: string): string {
    // Helper to check if format contains any unescaped token from a token group
    const hasTimeUnit = (tokens: string[]): boolean => {
        let testFormat = format.replace(/\[([^\]]+)\]/g, '');
        return tokens.some(token => testFormat.includes(token));
    };

    // Initial values
    let totalSeconds = Math.floor(duration.asSeconds());

    // Calculate each unit, starting from largest shown unit
    let years = 0;
    let months = 0;
    let days = 0;
    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    if (hasTimeUnit(momentFormatTokens.year)) {
        years = Math.floor(totalSeconds / (365 * 24 * 60 * 60));
        totalSeconds %= (365 * 24 * 60 * 60);
    }

    if (hasTimeUnit(momentFormatTokens.month)) {
        months = Math.floor(totalSeconds / (30 * 24 * 60 * 60));
        totalSeconds %= (30 * 24 * 60 * 60);
    }

    if (hasTimeUnit(momentFormatTokens.day)) {
        days = Math.floor(totalSeconds / (24 * 60 * 60));
        totalSeconds %= (24 * 60 * 60);
    }

    if (hasTimeUnit(momentFormatTokens.hour)) {
        hours = Math.floor(totalSeconds / (60 * 60));
        totalSeconds %= (60 * 60);
    }

    if (hasTimeUnit(momentFormatTokens.minute)) {
        minutes = Math.floor(totalSeconds / 60);
        totalSeconds %= 60;
    }

    if (hasTimeUnit(momentFormatTokens.second)) {
        seconds = totalSeconds;
    }

    // Temporarily replace escaped content with unique placeholders
    const escapeBlocks = new Map<string, string>();
    let counter = 0;
    let processedFormat = format.replace(/\[([^\]]+)\]/g, (match) => {
        const placeholder = `__ESCAPE_${counter}__`;
        escapeBlocks.set(placeholder, match.slice(1, -1)); // Store without brackets
        counter++;
        return placeholder;
    });

    // Replace tokens in format string
    momentFormatTokens.year.forEach(token => {
        processedFormat = processedFormat.replace(new RegExp(token, 'g'), String(years));
    });
    
    momentFormatTokens.month.forEach(token => {
        processedFormat = processedFormat.replace(new RegExp(token, 'g'), String(months));
    });

    momentFormatTokens.day.forEach(token => {
        processedFormat = processedFormat.replace(new RegExp(token, 'g'), String(days));
    });

    momentFormatTokens.hour.forEach(token => {
        processedFormat = processedFormat.replace(new RegExp(token, 'g'), String(hours));
    });

    momentFormatTokens.minute.forEach(token => {
        processedFormat = processedFormat.replace(new RegExp(token, 'g'), String(minutes));
    });

    momentFormatTokens.second.forEach(token => {
        processedFormat = processedFormat.replace(new RegExp(token, 'g'), String(seconds));
    });

    // Restore escaped content
    escapeBlocks.forEach((value, placeholder) => {
        processedFormat = processedFormat.replace(placeholder, value);
    });

    // Clean up spaces
    return processedFormat.replace(/\s+/g, ' ').trim();
}

function doSort<T extends CIReferral | CICase>(a: T, b: T, sortField: keyof T, sortOrder: 'asc' | 'desc' = 'asc'): number {
    const sortMap = {
        'asc': { 'true': 1, 'false': -1 },
        'desc': { 'true': -1, 'false': 1 }
    };
    const [ A, B ] = getObjectComparators<T>(a, b, sortField);
    if (A === B) return 0;
    return sortMap[sortOrder][`${A > B}`];
}

export function sortCIReferrals(referrals: (CIReferral)[] = [], sorts: ISortsDefinition<CIReferral> = { bookingStartTime: 'asc' }): (CIReferral)[] {
    let sortedData = referrals.slice();

    if (sortedData.length) {
        for (const fieldName in sorts) {
            const order = sorts[fieldName];
            sortedData = sortedData
                // if desc, get accepted referrals first
                .filter((referral) => order === 'desc' ? referral.status === CIReferralStatus.Accepted : false).sort((a, b) => doSort(a, b, fieldName as keyof CIReferral, order))
                // get open referrals next
                .concat(referrals.filter((referral) => referral.status === CIReferralStatus.Open).sort((a, b) => doSort(a, b, fieldName as keyof CIReferral, order)))
                // if asc, append accepted referrals at the end
                .concat(referrals.filter((referral) => order === 'asc' ? referral.status === CIReferralStatus.Accepted : false).sort((a, b) => doSort(a, b, fieldName as keyof CIReferral, order)));
        }
    }

    return sortedData;
}

export function sortCICases(cases: (CICase)[] = [], order: 'asc' | 'desc' = 'asc'): (CICase)[] {
    const now = Date.now();
    const reverseOrder = order === 'asc' ? 'desc' : 'asc';
    // get assigned cases
    const ciCases = cases.slice();
    return ciCases      
        // if desc, get active/completed cases first
        .filter((ciCase) => order === 'desc' ? moment(ciCase.interventionStartDateTime).isSameOrBefore(now) : false).sort((a, b) => doSort(a, b, 'interventionStartDateTime', reverseOrder))
        // get future cases next
        .concat(ciCases.filter((ciCase) => moment(ciCase.interventionStartDateTime).isAfter(now)).sort((a, b) => doSort(a, b, 'interventionStartDateTime', order)))
        // if asc, append active/completed cases at the end
        .concat(ciCases.filter((ciCase) => order === 'asc' ? moment(ciCase.interventionStartDateTime).isSameOrBefore(now) : false).sort((a, b) => doSort(a, b, 'interventionStartDateTime', reverseOrder)));
}

